import { useEffect, useLayoutEffect, useState } from "react";
import { Zap } from "lucide-react";
import { sampleSmooth, setHandle, smoothPath, snapTo, taperPath, type Pt } from "../lib/curve";
import { useSize } from "../lib/useSize";

/**
 * The hero: an event's "chance by date" curve, drawn as the brand mark and dragged like the app's editor.
 *
 * The curve is an illustration. Its handles are the only numbers the visitor changes, and every figure shown is read
 * straight from a handle (or is 100 minus one), so the page does no modelling (root CLAUDE.md, rule 1).
 */

/** Months from Sep 2026 ("now") to Dec 2032. */
const SPAN = 75;
const YEARS = [2027, 2028, 2029, 2030, 2031, 2032];
/** Month index of 1 January of a year. */
const jan = (y: number) => (y - 2026) * 12 - 8;

type Key = { m: number; end: string; short: string };
const KEYS: Key[] = [
	{ m: 3, end: "end of 2026", short: "Dec 2026" },
	{ m: 15, end: "end of 2027", short: "Dec 2027" },
	{ m: 27, end: "end of 2028", short: "Dec 2028" },
	{ m: 51, end: "end of 2030", short: "Dec 2030" },
	{ m: 75, end: "end of 2032", short: "Dec 2032" },
];
const START = [3, 8, 17, 44, 61];
/** The handle the headline reads, and the one that invites the first drag. */
const HEADLINE = 2;

/** A prediction market's date ladder: rungs at their own dates, sized by volume. It has no view after Dec 2028. */
const MARKET: { m: number; v: number; vol: number }[] = [
	{ m: 3, v: 4, vol: 0.55 },
	{ m: 9, v: 7, vol: 0.8 },
	{ m: 15, v: 11, vol: 1 },
	{ m: 21, v: 16, vol: 0.6 },
	{ m: 27, v: 22, vol: 0.45 },
];
const marketAt = (m: number) => MARKET.find((r) => r.m === m)?.v;

/** Handle radii follow the mark: small at the thin start, large at the heavy end (4, 4.7, 5.6, 6, 6 in the 64 box). */
const RADII = [4, 4.7, 5.6, 6, 6];

export function HeroChart({ className, behind }: { className?: string; behind?: React.RefObject<HTMLElement | null> }) {
	const [wrapRef, { w: W, h: H }] = useSize<HTMLDivElement>();
	const [values, setValues] = useState(START);
	const [drag, setDrag] = useState<number | null>(null);
	const [focus, setFocus] = useState<number | null>(null);
	const [touched, setTouched] = useState(false);
	const [released, setReleased] = useState(0);
	const [note, setNote] = useState(false);

	// Where the headline sits over the chart: the curve fades there, so the text stays readable (the handles do not).
	const [shade, setShade] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
	useLayoutEffect(() => {
		const el = behind?.current;
		const wrap = wrapRef.current;
		if (!el || !wrap || W === 0) return;
		const a = el.getBoundingClientRect();
		const b = wrap.getBoundingClientRect();
		const overlaps = a.bottom > b.top && a.right > b.left;
		setShade(overlaps ? { x: a.left - b.left, y: a.top - b.top, w: a.width, h: a.height } : null);
	}, [behind, wrapRef, W, H]);

	const compact = W < 640;
	const L = compact ? 6 : 10;
	const R = compact ? 40 : 52;
	const T = compact ? 20 : 30;
	const B = compact ? 34 : 40;
	const iw = Math.max(1, W - L - R);
	const ih = Math.max(1, H - T - B);
	const x = (m: number) => L + (m / SPAN) * iw;
	const y = (v: number) => T + (1 - v / 100) * ih;
	const vAt = (py: number) => Math.round((1 - (py - T) / ih) * 100);

	const pts: Pt[] = [[x(0), y(0)], ...KEYS.map((k, i): Pt => [x(k.m), y(values[i])])];
	const samples = sampleSmooth(pts, 28);
	const scale = compact ? 1.25 : 1.7;
	const line = W > 0 ? taperPath(samples, compact ? 0.9 : 1.2, compact ? 3 : 4.2) : "";
	const area = W > 0 ? `${smoothPath(pts)}L${x(SPAN)} ${y(0)}L${x(0)} ${y(0)}Z` : "";

	const set = (i: number, v: number) => setValues((vs) => setHandle(vs, i, snapTo(v, marketAt(KEYS[i].m)), 0, 100));

	const toLocal = (e: React.PointerEvent) => {
		const r = wrapRef.current!.getBoundingClientRect();
		return { px: e.clientX - r.left, py: e.clientY - r.top };
	};
	const start = (i: number, e: React.PointerEvent) => {
		e.preventDefault();
		(e.currentTarget as Element).setPointerCapture(e.pointerId);
		setDrag(i);
		setTouched(true);
		set(i, vAt(toLocal(e).py));
	};
	/** A mouse press anywhere on the plot takes the nearest key date's handle. Touch must grab a handle, so the page still scrolls. */
	const startNearest = (e: React.PointerEvent) => {
		if (e.pointerType === "touch") return;
		const { px } = toLocal(e);
		let best = 0;
		KEYS.forEach((k, i) => {
			if (Math.abs(x(k.m) - px) < Math.abs(x(KEYS[best].m) - px)) best = i;
		});
		start(best, e);
	};
	const move = (e: React.PointerEvent) => {
		if (drag === null) return;
		set(drag, vAt(toLocal(e).py));
	};
	const end = () => {
		if (drag === null) return;
		setDrag(null);
		setNote(true);
		setReleased((n) => n + 1);
	};

	const onKey = (i: number, e: React.KeyboardEvent) => {
		const step = e.shiftKey ? 5 : 1;
		const deltas: Record<string, number> = { ArrowUp: step, ArrowRight: step, ArrowDown: -step, ArrowLeft: -step, PageUp: 10, PageDown: -10 };
		if (e.key in deltas) {
			e.preventDefault();
			setTouched(true);
			setValues((vs) => setHandle(vs, i, vs[i] + deltas[e.key], 0, 100));
		} else if (e.key === "Home" || e.key === "End") {
			e.preventDefault();
			setTouched(true);
			setValues((vs) => setHandle(vs, i, e.key === "Home" ? 0 : 100, 0, 100));
		}
	};

	// The note under the chart says what the app does on release; it fades after a while.
	useEffect(() => {
		if (released === 0) return;
		const t = window.setTimeout(() => setNote(false), 5200);
		return () => window.clearTimeout(t);
	}, [released]);

	const active = drag ?? focus;
	const lastMarket = MARKET[MARKET.length - 1];
	const headline = values[HEADLINE];
	const market = marketAt(KEYS[HEADLINE].m)!;

	return (
		<div className={className}>
			{/* The event, as its page in the app names it. */}
			<div className="pointer-events-none relative z-10 mb-3 lg:absolute lg:top-6 lg:right-[52px] lg:mb-0 lg:max-w-[330px] lg:text-right">
				<div className="flex items-center gap-1.5 text-sm font-medium text-ink lg:justify-end">
					<Zap size={15} strokeWidth={2.2} className="text-quiet" aria-hidden />
					AI equity crash
				</div>
				<p className="voice mt-0.5 text-[17px] leading-snug text-eggplant">When will AI stocks close 30% below their high, and stay there?</p>
				<div className="mt-2.5 flex items-baseline gap-2 lg:justify-end" aria-live="polite">
					<span className="tnum text-[40px] leading-none font-semibold tracking-[-0.03em] text-peri-deeper">{headline}%</span>
					<span className="text-sm text-eggplant">chance by end of 2028</span>
				</div>
				<div className="mt-1.5 flex flex-wrap gap-x-3 text-xs text-quiet lg:justify-end">
					<span>
						Market <span className="tnum font-medium text-market">{market}%</span>
					</span>
					<span className="tnum">{100 - values[KEYS.length - 1]}% it hasn’t happened by 2032</span>
				</div>
			</div>

			<div
				ref={wrapRef}
				className="relative h-[330px] sm:h-[380px] lg:absolute lg:inset-0 lg:h-auto"
				onPointerMove={move}
				onPointerUp={end}
				onPointerCancel={end}
			>
				{W > 0 && H > 0 && (
					<svg width={W} height={H} className="absolute inset-0 select-none" aria-hidden>
						<defs>
							<linearGradient id="hero-area" x1="0" x2="0" y1="0" y2="1">
								<stop offset="0" stopColor="var(--brand-periwinkle)" stopOpacity="0.16" />
								<stop offset="1" stopColor="var(--brand-periwinkle)" stopOpacity="0.02" />
							</linearGradient>
							<filter id="hero-soft" x="-20%" y="-20%" width="140%" height="140%">
								<feGaussianBlur stdDeviation="18" />
							</filter>
							<mask id="hero-shade" maskUnits="userSpaceOnUse" x={0} y={0} width={W} height={H}>
								<rect width={W} height={H} fill="#fff" />
								{shade && (
									<rect x={shade.x - 12} y={shade.y} width={shade.w + 24} height={shade.h + 8} fill="#000" fillOpacity={0.62} filter="url(#hero-soft)" />
								)}
							</mask>
							<clipPath id="hero-reveal">
								<rect x={0} y={0} width={W} height={H} className="origin-left animate-[reveal-x_1500ms_cubic-bezier(0.35,0,0.2,1)_250ms_both]" />
							</clipPath>
						</defs>

						{/* Mouse press anywhere on the plot takes the nearest handle. */}
						<rect x={L} y={T} width={iw} height={ih} fill="transparent" onPointerDown={startNearest} className="cursor-ns-resize" />

						{/* Axes: chance on the right, years along the foot, "now" on the left. */}
						<g className="pointer-events-none" fontFamily="var(--font-mono)" fontSize={compact ? 10 : 11} fill="var(--quiet)">
							{[25, 50, 75, 100].map((v) => (
								<g key={v}>
									<line x1={L} x2={x(SPAN)} y1={y(v)} y2={y(v)} stroke="var(--line)" strokeDasharray="2 5" />
									<text x={x(SPAN) + 10} y={y(v) + 4}>
										{v}%
									</text>
								</g>
							))}
							<line x1={L} x2={x(SPAN)} y1={y(0)} y2={y(0)} stroke="var(--ink)" strokeOpacity={0.35} />
							<text x={x(SPAN) + 10} y={y(0) + 4}>
								0%
							</text>
							{YEARS.map((yr) => (
								<g key={yr}>
									<line x1={x(jan(yr))} x2={x(jan(yr))} y1={y(0)} y2={y(0) + 5} stroke="var(--ink)" strokeOpacity={0.35} />
									{(!compact || yr % 2 === 0) && (
										<text x={x(jan(yr))} y={y(0) + 20} textAnchor="middle">
											{yr}
										</text>
									)}
								</g>
							))}
							<line x1={x(0)} x2={x(0)} y1={y(0)} y2={y(0) + 5} stroke="var(--ink)" strokeOpacity={0.35} />
							<text x={x(0)} y={y(0) + 20}>
								now
							</text>
						</g>

						{/* The market ladder, faint behind the curve, stopping at its last rung. */}
						<g className="pointer-events-none animate-[fade-in_600ms_ease-out_1500ms_both]">
							<path
								d={smoothPath(MARKET.map((r): Pt => [x(r.m), y(r.v)]))}
								fill="none"
								stroke="var(--market)"
								strokeOpacity={0.35}
								strokeDasharray="1 4"
								strokeLinecap="round"
								strokeWidth={1.5}
							/>
							{MARKET.map((r) => (
								<circle key={r.m} cx={x(r.m)} cy={y(r.v)} r={(compact ? 2.4 : 3) + r.vol * (compact ? 1.6 : 2.2)} fill="var(--market)" fillOpacity={0.85} />
							))}
							{!compact && (
								<text
									x={x(lastMarket.m) + 12}
									y={values[HEADLINE] < lastMarket.v ? y(lastMarket.v) - 10 : y(lastMarket.v) + 20}
									fontSize={12}
									fill="var(--market)"
								>
									Market has no view after Dec 2028
								</text>
							)}
						</g>

						{/* The forecast: the brand's tapered line, revealed left to right on load. */}
						<g clipPath="url(#hero-reveal)" className="pointer-events-none">
							<path d={area} fill="url(#hero-area)" />
							<path d={line} fill="var(--brand-periwinkle)" mask="url(#hero-shade)" />
						</g>

						{/* A drop line from the dragged handle to its date. */}
						{active !== null && (
							<line
								x1={x(KEYS[active].m)}
								x2={x(KEYS[active].m)}
								y1={y(values[active])}
								y2={y(0)}
								stroke="var(--brand-periwinkle)"
								strokeOpacity={0.5}
								strokeDasharray="2 3"
								className="pointer-events-none"
							/>
						)}
					</svg>
				)}

				{/* The handles sit above the headline, so a handle dragged under the text stays in reach. */}
				{W > 0 && H > 0 && (
					<svg
						width={W}
						height={H}
						className="pointer-events-none absolute inset-0 z-20 select-none"
						role="group"
						aria-label="Chance by date for an AI equity crash. Drag a handle, or focus one and use the arrow keys."
					>
						{KEYS.map((k, i) => {
							const cx = x(k.m);
							const cy = y(values[i]);
							const r = RADII[i] * scale;
							const delay = 250 + (k.m / SPAN) * 1350;
							return (
								<g
									key={k.m}
									role="slider"
									tabIndex={0}
									aria-label={`Chance by ${k.end}`}
									aria-valuemin={0}
									aria-valuemax={100}
									aria-valuenow={values[i]}
									aria-valuetext={`${values[i]}% chance by ${k.end}`}
									onPointerDown={(e) => start(i, e)}
									onKeyDown={(e) => onKey(i, e)}
									onFocus={() => setFocus(i)}
									onBlur={() => setFocus(null)}
									className="pointer-events-auto cursor-grab outline-none active:cursor-grabbing"
									style={{ touchAction: "none" }}
								>
									<circle cx={cx} cy={cy} r={22} fill="transparent" />
									{i === HEADLINE && !touched && (
										<circle
											cx={cx}
											cy={cy}
											r={r + 2}
											fill="none"
											stroke="var(--brand-periwinkle)"
											strokeWidth={2}
											className="animate-[hint-pulse_2.2s_ease-out_2200ms_infinite] [transform-box:fill-box] [transform-origin:center]"
											style={{ opacity: 0 }}
										/>
									)}
									{focus === i && <circle cx={cx} cy={cy} r={r + 6} fill="none" stroke="var(--brand-periwinkle)" strokeOpacity={0.45} strokeWidth={2} />}
									<g
										className="[transform-box:fill-box] [transform-origin:center]"
										style={{ animation: `handle-in 380ms cubic-bezier(0.3,1.4,0.5,1) ${delay}ms both` }}
									>
										<circle cx={cx} cy={cy} r={drag === i ? r + 1.5 : r} fill="var(--paper)" stroke="var(--brand-periwinkle)" strokeWidth={compact ? 2.5 : 3} />
									</g>
								</g>
							);
						})}
					</svg>
				)}

				{/* The dragged handle's value, in the product's words. */}
				{active !== null && W > 0 && (
					<div
						className="pointer-events-none absolute z-30 -translate-x-1/2 -translate-y-full rounded-sm bg-ink px-2.5 py-1.5 text-xs whitespace-nowrap text-white shadow-lg"
						style={{ left: Math.min(Math.max(x(KEYS[active].m), 90), W - 90), top: y(values[active]) - RADII[active] * scale - 10 }}
					>
						<span className="tnum font-semibold">{values[active]}%</span> by {KEYS[active].end}
						{marketAt(KEYS[active].m) === values[active] && <span className="text-[#9dbcf0]"> · matches the market</span>}
					</div>
				)}

				{/* The first invitation, beside the handle it means. */}
				{W > 0 && !compact && !touched && (
					<span
						className="pointer-events-none absolute z-20 animate-[fade-in_500ms_ease-out_2000ms_both] text-sm text-peri-deeper"
						style={{ left: x(KEYS[HEADLINE].m) + 20, top: y(values[HEADLINE]) + 12 }}
					>
						Drag a handle
					</span>
				)}
			</div>
			<p
				className={`relative z-10 mt-3 min-h-[1.4em] text-xs transition-opacity duration-500 lg:absolute lg:right-[52px] lg:-bottom-8 lg:mt-0 lg:text-right ${!touched || note ? "opacity-100" : "opacity-0"} ${touched ? "text-quiet" : "text-peri-deeper lg:hidden"}`}
				aria-live="polite"
			>
				{touched ? "In the app, letting go saves the forecast and reruns everything that depends on it." : "Drag a handle to change the forecast."}
			</p>
		</div>
	);
}
